import { GlobalStyles } from '@mote/config/defaults/GlobalStyles'
import {
  sentryEnrichEvent,
  SENTRY_COMMON_ERROR_IGNORE_LIST
} from '@mote/config/external/sentry.config'
import { useExtensionInstalled } from '@mote/helpers'
import {
  EXTENSION_INSTALLED_STORAGE_KEY,
  __PRD__
} from '@mote/helpers/constants'
import { getExtensionVersion } from '@mote/helpers/extension'
import { LocaleProvider } from '@mote/l10n/providers/LocaleProvider'
import {
  checkAndUpdateStorageVersion,
  RecordReplySidebarContext,
  useRecordReplySidebarState
} from '@mote/presentation'
import { FlightRecorderProvider } from '@mote/presentation/src/recorder/state/FlightRecorderContext'
import { UserAgentProvider } from '@quentin-sommer/react-useragent'
import * as Sentry from '@sentry/browser'
import { Integrations } from '@sentry/tracing'
import 'focus-visible'
import React from 'react'
import 'react-app-polyfill/ie9'
import 'react-app-polyfill/stable'
import ReactGA from 'react-ga'
import { BrowserRouter as Router } from 'react-router-dom'
import { getWebVersion } from 'web/src/shared/getWebVersion'
import { AuthProvider } from './auth/AuthContext'
import { WebRouting } from './Routing'
import {
  LocalStoragePermissionProvider,
  useCanAccessLocalStorage
} from '@mote/helpers/browser'

ReactGA.initialize('UA-156510801-1')
ReactGA.pageview(window.location.pathname + window.location.search)

Sentry.init({
  dsn: 'https://70d7009086954f558c4f2f3e4e977bb6@sentry.io/5181853',
  integrations: [new Integrations.BrowserTracing()],
  ignoreErrors: [...SENTRY_COMMON_ERROR_IGNORE_LIST],
  beforeSend(event, hint) {
    if (!__PRD__) {
      return null
    }

    if (hint?.originalException) {
      if (event) {
        event.extra = Object.assign(
          {},
          event.extra ?? {},
          sentryEnrichEvent(hint.originalException)
        )
      }
    }

    if (event?.exception?.values?.length === 1) {
      const [exception] = event.exception.values
      if (
        exception.type === 'UnhandledRejection' &&
        exception.value.includes('Object Not Found Matching Id')
      ) {
        return null
      }
    }

    return event
  }
})

Sentry.setTag('extensionVersion', getExtensionVersion())

export const MoteAppEntryPoint = () => {
  const [loading, setLoading] = React.useState(true)
  const replyRecordSidebarState = useRecordReplySidebarState()
  const extensionInstalled = useExtensionInstalled()
  const canAccessLocalStorage = useCanAccessLocalStorage()

  React.useEffect(() => {
    const loader = document.querySelector('#preloader')

    let removeTimeoutId
    if (loader) {
      loader.classList.add('hidden')
      removeTimeoutId = setTimeout(() => {
        loader.remove()

        document.querySelector('#preloader-styles')?.remove()
      }, 1000)
    }

    return () => {
      if (removeTimeoutId) clearTimeout(removeTimeoutId)
    }
  }, [])

  React.useEffect(() => {
    if (!canAccessLocalStorage) return
    if (extensionInstalled === null) return

    window.localStorage.setItem(
      EXTENSION_INSTALLED_STORAGE_KEY,
      JSON.stringify(extensionInstalled)
    )
  }, [extensionInstalled, canAccessLocalStorage])

  React.useEffect(() => {
    checkAndUpdateStorageVersion()
      .then(() => setLoading(false))
      .catch(err => {
        console.error(err.message)
        setLoading(false)
      })
    console.log('v:', getWebVersion())
  }, [])

  if (loading) return null

  if (
    window.location.host.includes('justmote.me') ||
    (window.location.host.includes('mote.com') &&
      !window.location.host.includes('www') &&
      !window.location.host.includes('dev'))
  ) {
    const newDomainToRedirectTo =
      window.location.host === 'support.justmote.me'
        ? 'https://support.mote.com'
        : 'https://www.mote.com'

    window.location.replace(
      `${newDomainToRedirectTo}${window.location.pathname}${window.location.search}${window.location.hash}`
    )

    return null
  }

  return (
    <Router>
      <GlobalStyles />
      <LocalStoragePermissionProvider>
        <UserAgentProvider ua={window.navigator.userAgent}>
          <RecordReplySidebarContext.Provider value={replyRecordSidebarState}>
            <AuthProvider>
              <LocaleProvider>
                <FlightRecorderProvider>
                  <WebRouting />
                </FlightRecorderProvider>
              </LocaleProvider>
            </AuthProvider>
          </RecordReplySidebarContext.Provider>
        </UserAgentProvider>
      </LocalStoragePermissionProvider>
    </Router>
  )
}
